import * as React from "react";
import ReactDOM from "react-dom";
import { BackButtonListener } from "nx-components/BackButtonResponder";
import enqueueIn from "nx-components/utils/enqueueIn";
import Dispatch from "nexia_mobile/flux_like/dispatch";
import { closeAllDestroyableDialogs } from "nexia_mobile/components/Dialogs/renderInDestroyableRoot";
import Store, {
  setValueAtPath,
  resetStore,
} from "nexia_mobile/flux_like/store";
import AppState from "nexia_mobile/models/app_state";
import Platform from "nexia_mobile/platform";
import LocalSettings, { AUTH_TYPES } from "nexia_mobile/models/local_settings";
import { ResourceMap } from "nexia_mobile/framework/resource";
import AjaxHooks from "nexia_mobile/ajax_hooks";
import Session from "nexia_mobile/models/session";
import DemoSession from "nexia_mobile/models/demo_session";
import CommercialDemoSession from "nexia_mobile/models/commercial_demo_session";
import VERSION from "nexia_mobile/version";
import { history, clearHistoryAndPush } from "nexia_mobile/history";
import { currentlyConfiguring724 } from "nexia_mobile/api/xr724";
import { currentlyConfiguringDragonfly } from "nexia_mobile/api/dragonfly";
import App from "nexia_mobile/components/App";
import Environment from "nexia_mobile/environment";
import EventStream from "nexia_mobile/event_stream";

const showWebView = () => {
  enqueueIn(() => {
    // Wait a tick to make sure all layout issues are resolved. We use enqueueIn
    // to work around edge cases where an invisible webview never fires setTimeout
    Platform.showWebView();
  }, 0);
};

const setInitialUserAuthenticated = () => {
  const needsAuth =
    !LocalSettings.isActivated() &&
    LocalSettings.getCurrentAuth() !== AUTH_TYPES.NONE &&
    LocalSettings.needsPin();
  setValueAtPath("appState.userAuthenticated", !needsAuth);
};

Dispatch.on("APP_LAUNCH", () => {
  Dispatch("INIT_CONNECTION_TRACKING");
  AjaxHooks.init();

  const listener = new BackButtonListener();
  BackButtonListener.setSingleton(listener);
  listener.startListening();
  Dispatch.listenTo(Platform, Platform.Event.APP_PAUSE, () =>
    Dispatch("APP_PAUSE")
  );
  Dispatch.listenTo(Platform, Platform.Event.APP_RESUME, () =>
    Dispatch("APP_RESUME")
  );
  Dispatch("START_LISTENING_TO_PLATFORM_EVENTS");
  Dispatch("START_LISTENING_TO_PLATFORM_BACK_BUTTON");

  if (!Platform.appVersion) {
    // On Mobile platforms, the native sets the app version.
    // We want to always have a version, even in the browser,
    // because the server cares about it.
    let numericVersion = VERSION.split("-")[0];
    Platform.setAppVersion(numericVersion);
  }
  if (Store.appState) {
    Store.appState.stopListening();
  }
  setValueAtPath("appState", new AppState());
  setValueAtPath("session", new Session());
  setInitialUserAuthenticated();

  if (LocalSettings.isActivated()) {
    Dispatch("SETUP_LOCATION_POLLING");
  }
  $("body")
    .find(".spinner-background")
    .remove();

  setValueAtPath(
    "isAuthAttemptLockedOut",
    LocalSettings.isAuthAttemptLockedOut()
  );
  Dispatch("APP_MOUNT_REACT");
  showWebView();
});

Dispatch.on("APP_MOUNT_REACT", () => {
  document.body.innerHTML = '<div id="root"></div>';
  ReactDOM.render(<App />, document.querySelector("#root"));
});

Dispatch.on("APP_RESET_PASSWORD_CODE", (resetPasswordCode) => {
  clearHistoryAndPush("/reset-password");
  setValueAtPath("resetPasswordCode", resetPasswordCode);
});

Dispatch.on("APP_TRANSFER_ACCOUNT_CODE", (transferAccountCode) => {
  clearHistoryAndPush("/transfer-account/user-info");
  setValueAtPath("transferAccountCode", transferAccountCode);
});

Dispatch.on("APP_TRANSFER_ACCOUNT_SUCCESS", (message) => {
  clearHistoryAndPush("/welcome");
  Dispatch("SHOW_TOAST", message);
});

Dispatch.on("APP_PAUSE", () => {
  setValueAtPath("isAppPaused", true);
  setValueAtPath("appLastPausedAt", Date.now());
  Dispatch("STOP_VOGNITION_LISTENER");
  EventStream.pause();
});

function pausedLongEnoughAgoThatAuthorizationIsRequired() {
  const oneHour = 60 * 60 * 1000;
  const oneMinute = 1 * 60 * 1000;
  const longEnoughAgo =
    currentlyConfiguring724() || currentlyConfiguringDragonfly()
      ? // Increase the amount of time the app must be paused for before pin entry
        // is shown, because if we show pin entry while they are configuring a 724,
        // when they finish pin entry, it will go to the dashboard, instead of to
        // the in-progress enrollment. Plus, the user has to switch back and forth
        // between the app and the phone's WiFi settings as part of the
        // configuration process, and it'd be annoying to have to put their pin in
        // while doing that.
        oneHour
      : oneMinute;
  return (
    Store.appLastPausedAt && Date.now() - longEnoughAgo > Store.appLastPausedAt
  );
}

Dispatch.on("APP_RESUME", () => {
  setValueAtPath("isAppPaused", false);
  Dispatch("CHECK_NETWORK_STATUS");

  if (
    LocalSettings.isActivated() &&
    pausedLongEnoughAgoThatAuthorizationIsRequired()
  ) {
    Dispatch("APP_SOFT_RESTART");
  } else {
    EventStream.resume();
  }

  showWebView();
});

// The app was paused for long enough that we want to treat the pause/resume
// that just occurred more like a restart. So things that happen "at launch"
// should happen now.
Dispatch.on("APP_SOFT_RESTART", () => {
  setValueAtPath("userCancelledFingerprint", false);
  setValueAtPath("vognition.listenOnStartupPending", true);

  const shouldReauthorize =
    !LocalSettings.canSkipLogin() && !Store.appState.inDemoMode;

  if (shouldReauthorize) {
    Dispatch("APP_REAUTHORIZE_FROM_RESUME");
  } else {
    Dispatch("POPULATE_SESSION");
    Dispatch("START_VOGNITION_LISTENER_FROM_STARTUP");
  }
});

Dispatch.on("APP_LOG_IN", (mobileId, apiKey, setupStep, locale) => {
  if (setupStep === "signup_survey") {
    history.push("/signup/survey", {
      mobileId,
      apiKey,
    });
  } else if (setupStep === "promo_code") {
    history.push("/signup/promo-code", {
      mobileId,
      apiKey,
    });
  } else {
    LocalSettings.setMobileIdAndApiKey(mobileId, apiKey);
    LocalSettings.setLocale(locale);
    setInitialUserAuthenticated();
    setValueAtPath("session", new Session());
    Dispatch("SETUP_LOCATION_POLLING");
    clearHistoryAndPush("/");
  }
});

Dispatch.on("APP_LOG_OUT", (options = {}) => {
  ReactDOM.unstable_batchedUpdates(() => {
    EventStream.end();
    if (options.failedAuthentication) {
      Dispatch("CANCEL_FINGERPRINT");
    } else {
      Store.session.destroy({ silent: true });
    }
    Store.appState.stopListening();
    AjaxHooks.abortAllActiveRequests();
    ResourceMap.clearAll();
    Dispatch("TEARDOWN_LOCATION_POLLING");
    Platform.clearSessionCache();
    Platform.resetAppBadge();
    const locale = LocalSettings.getLocale();
    LocalSettings.clear();
    LocalSettings.setLocale(locale);
    resetStore();

    setValueAtPath("appState", new AppState());
    setValueAtPath("session", new Session());

    clearHistoryAndPush("/");
  });
});

Dispatch.on("APP_ENTER_DEMO_MODE", () => {
  Platform.sendGoogleAnalyticsEvent("Dispatch", "APP_ENTER_DEMO_MODE");
  Store.appState.stopListening();
  setValueAtPath(
    "appState",
    new AppState({ inDemoMode: true, isFirstVisit: true })
  );
  setValueAtPath(
    "session",
    Environment.isCommercial() ? new CommercialDemoSession() : new DemoSession()
  );
  Dispatch("POPULATE_SESSION");
  setValueAtPath("demoModeEvents", {});

  clearHistoryAndPush("/");
});

Dispatch.on("APP_FIRST_DEMO_VISIT", () => {
  Store.appState.isFirstVisit = false;
});

Dispatch.on("APP_REAUTHORIZE_FROM_RESUME", () => {
  // Close any dialogs that are not automatically unmounted so they don't cover auth.
  closeAllDestroyableDialogs();
  clearHistoryAndPush("/");
});

Dispatch.on("SCREEN_READER_RUNNING_CHANGED", (newValue) => {
  setValueAtPath("isScreenReaderRunning", newValue);
});



// WEBPACK FOOTER //
// ./src/flux_like/action_handlers/app.js